import * as React from 'react';
import type { EntityDetail } from '@web-config-app/core';
import type { EntityReferenceControlProps } from '@web-config-app/entity-form';
import {
  useAnnotatedSchemaProps,
  useEntityDetailsProps,
  getComposedSchemaPropertyPath,
  useReferencedEntityData,
  formatControlStatus,
} from '@web-config-app/entity-form';
import { useAddEntityById } from '../internals/use-add-entity-by-id.hook';
import { useEntityReferenceControlState } from '../internals/use-entity-reference-control-state';
import { EntitySelectControlPresenter } from '../internals/entity-select-control-presenter.component';
import { useEntityReferenceControlContext } from '../internals/use-entity-reference-control-context';

/**
 * Renders for schemas that have a defined `x-entity-reference.domainEntityType` value. This control can operate on
 * entity references that are pure domainEntityIds
 */

export const DomainEntityReferenceControlWrapper: React.FC<
  EntityReferenceControlProps
> = ({
  schema,
  handleChange,
  path,
  data: instanceId,
  referencedEntity,
  enabled,
  domainEntityAttributesQuery,
  errors,
}) => {
  const isArrayItem = useEntityReferenceControlContext();
  const { formPath, currentAuthoringEnvironment, isUsingConfigAppBackend } =
    useEntityDetailsProps();

  const { label, hint, banner } = useAnnotatedSchemaProps(
    schema,
    null,
    getComposedSchemaPropertyPath(formPath, path),
  );
  const { statusMessage, inputStatus } = formatControlStatus({
    errors,
    label,
  });
  /**
   * Grab props to be pass to the AddItemByIdModal component that allows a user to enter an entity ID
   */
  const {
    idValue,
    setIdValue,
    findEntityById,
    isLoading: isLoadingByIdSubmission,
    isError: isLoadingByIdError,
  } = useAddEntityById(referencedEntity, {
    enabled: false,
    retry: 1,
  });

  /**
   * `useReferencedEntityData` allows us to retrieve the name and status of the entity to display
   * in the control.
   */

  const {
    referencedEntity: entityReferenceData,
    isLoading,
    isError,
  } = useReferencedEntityData({
    domainEntity: referencedEntity,
    instanceId,
    environment: currentAuthoringEnvironment,
    isRelationshipReference: false,
  });

  const {
    closeAddById,
    closeChooseFromList,
    openChooseFromList,
    addItemTriggerList,
    isAddingById,
    isAddingFromList,
  } = useEntityReferenceControlState({
    isUsingConfigAppBackend,
    domainEntity: referencedEntity,
  });

  const handleValueChange = (entityDetail: EntityDetail) => {
    handleChange(path, entityDetail.id);
  };

  const onSubmitAddById = () => {
    findEntityById({
      onSuccess: ({ data: response }) => {
        const entityDetail = response.data;
        handleValueChange(entityDetail);
        closeAddById();
      },
    });
  };

  const onSubmitChooseFromList = (entityDetail: EntityDetail) => {
    handleValueChange(entityDetail);
    closeChooseFromList();
  };

  const onClearEntityReference = () => {
    handleChange(path, undefined);
  };

  return (
    <EntitySelectControlPresenter
      hint={hint}
      label={label!}
      statusMessage={statusMessage}
      inputStatus={inputStatus}
      banner={banner}
      enabled={enabled}
      entityReferenceData={entityReferenceData}
      isLoading={isLoading}
      isError={isError}
      isLoadingByIdSubmission={isLoadingByIdSubmission}
      isLoadingByIdError={isLoadingByIdError}
      onSubmitAddById={onSubmitAddById}
      onSubmitChooseFromList={onSubmitChooseFromList}
      addItemTriggerList={addItemTriggerList}
      isAddingById={isAddingById}
      isAddingFromList={isAddingFromList}
      closeAddById={closeAddById}
      closeChooseFromList={closeChooseFromList}
      domainEntity={referencedEntity}
      openChooseFromList={openChooseFromList}
      idValue={idValue}
      setIdValue={setIdValue}
      isArrayItem={isArrayItem}
      onClearEntityReference={onClearEntityReference}
      domainEntityAttributesQuery={domainEntityAttributesQuery}
      isRelationshipReference={false}
    />
  );
};
